import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import './WhatsAppRequestModal.css';

/**
 * WhatsAppRequestModal
 * Modal for shluchim to request WhatsApp updates
 * Low-pressure, minimal fields, no hype language
 */
const WhatsAppRequestModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    whatsappNumber: '',
    communityCity: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatPhoneNumber = (number) => {
    // Remove all non-numeric characters
    const cleaned = number.replace(/\D/g, '');
    
    // Add + prefix if not present
    if (cleaned && !cleaned.startsWith('1') && cleaned.length === 10) {
      return `+1${cleaned}`; // US number
    } else if (cleaned && !cleaned.startsWith('+')) {
      return `+${cleaned}`;
    }
    return cleaned ? `+${cleaned}` : '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.whatsappNumber.trim()) {
      setError('WhatsApp number is required');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch('/api/whatsapp-requests', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: formData.firstName.trim() || null,
          whatsappNumber: formatPhoneNumber(formData.whatsappNumber),
          communityCity: formData.communityCity.trim() || null
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to submit request');
      }

      setSubmitted(true);
      
      // Auto-close after 5 seconds
      setTimeout(() => {
        handleClose();
      }, 5000);
    } catch (err) {
      console.error('Error submitting WhatsApp request:', err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset form
    setFormData({
      firstName: '',
      whatsappNumber: '',
      communityCity: ''
    });
    setSubmitted(false);
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="whatsapp-modal-overlay" onClick={handleClose}>
      <div className="whatsapp-modal" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button className="whatsapp-modal-close" onClick={handleClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>

        {!submitted ? (
          <>
            {/* Header */}
            <div className="whatsapp-modal-header">
              <div className="whatsapp-modal-icon">
                <FontAwesomeIcon icon={faWhatsapp} />
              </div>
              <h2>Get WhatsApp Updates from Yesod</h2>
              <p className="whatsapp-modal-subtitle">
                This group is for shluchim who are using Yesod or exploring it. 
                We share product updates and real-world use — no noise and no pressure.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="whatsapp-modal-form">
              {error && (
                <div className="whatsapp-modal-error">
                  {error}
                </div>
              )}

              <div className="whatsapp-form-group">
                <label htmlFor="firstName">
                  First Name <span className="optional">(optional)</span>
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="Rabbi Moshe"
                  disabled={isSubmitting}
                />
              </div>

              <div className="whatsapp-form-group">
                <label htmlFor="whatsappNumber">
                  WhatsApp Number <span className="required">*</span>
                </label>
                <input
                  type="tel"
                  id="whatsappNumber"
                  name="whatsappNumber"
                  value={formData.whatsappNumber}
                  onChange={handleChange}
                  placeholder="+1 234 567 8900"
                  required
                  disabled={isSubmitting}
                />
                <small className="input-hint">Include country code (e.g., +1 for US)</small>
              </div>

              <div className="whatsapp-form-group">
                <label htmlFor="communityCity">
                  Community / City <span className="optional">(optional)</span>
                </label>
                <input
                  type="text"
                  id="communityCity"
                  name="communityCity"
                  value={formData.communityCity}
                  onChange={handleChange}
                  placeholder="Brooklyn, NY"
                  disabled={isSubmitting}
                />
              </div>

              <button 
                type="submit" 
                className="whatsapp-submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Request WhatsApp Access'}
              </button>
            </form>
          </>
        ) : (
          /* Success State */
          <div className="whatsapp-modal-success">
            <div className="success-icon">
              <FontAwesomeIcon icon={faWhatsapp} />
            </div>
            <h3>Thanks — you'll receive a message shortly to confirm before being added.</h3>
            <p>
              We'll send you a WhatsApp message to verify your number. 
              Once confirmed, you'll be added to the group.
            </p>
            <button className="whatsapp-close-btn" onClick={handleClose}>
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppRequestModal;
